import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  getAudioDevices,
  isAudioSupported,
  type WebAudioDevice,
} from "screens/audio-settings/audio-devices";

export type AudioConfig = {
  inputDevice: WebAudioDevice | null;
  sampleRate: number;
  channels: number;
};

const CONFIG_KEY = "alex_audio_config";

export const sampleRateOptions = [
  { value: 16_000, label: "16 kHz (Recommended for speech)" },
  { value: 44_100, label: "44.1 kHz" },
  { value: 48_000, label: "48 kHz" },
];

export const channelOptions = [
  { value: 1, label: "Mono — agent only" },
  { value: 2, label: "Stereo — agent + customer" },
];

const defaultAudioConfig: AudioConfig = {
  inputDevice: null,
  sampleRate: 16_000,
  channels: 1,
};

export const useAudio = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [devices, setDevices] = useState<WebAudioDevice[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [audioConfig, setAudioConfigState] = useState<AudioConfig>(() => {
    try {
      const savedConfig = localStorage.getItem(CONFIG_KEY);
      if (savedConfig) {
        const parsed = JSON.parse(savedConfig);
        return {
          ...defaultAudioConfig,
          sampleRate: parsed.sampleRate ?? defaultAudioConfig.sampleRate,
          channels: parsed.channels ?? defaultAudioConfig.channels,
        };
      }
      return defaultAudioConfig;
    } catch {
      return defaultAudioConfig;
    }
  });

  useEffect(() => {
    const handleAudioPermissionAndDevices = async () => {
      const audioDevices = await getAudioDevices();
      setDevices(audioDevices);

      try {
        const savedConfig = localStorage.getItem(CONFIG_KEY);
        if (!savedConfig) {
          return;
        }

        const parsed = JSON.parse(savedConfig);
        const savedDeviceId = parsed.inputDevice?.deviceId;
        if (!savedDeviceId) {
          return;
        }

        const savedDevice = audioDevices.find((d) => d.deviceId === savedDeviceId);
        if (savedDevice) {
          setAudioConfigState((prev) => ({
            ...prev,
            inputDevice: savedDevice,
          }));
        }
      } catch {
        // Ignore errors when restoring saved device
      }
    };

    const fetchAudioDevices = async () => {
      if (!isAudioSupported()) {
        setError("Audio recording is not supported in this browser");
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        await handleAudioPermissionAndDevices();
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load audio devices";
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAudioDevices();
  }, []);

  useEffect(() => {
    if (!audioConfig.inputDevice && devices.length > 0 && !isLoading) {
      const defaultDevice =
        devices.find((d) => d.label.toLowerCase().includes("voyc")) || devices[0];
      setAudioConfigState((prev) => ({
        ...prev,
        inputDevice: defaultDevice,
      }));
    }
  }, [audioConfig, devices, isLoading]);

  const onUpdateAudioConfig = (updates: Partial<AudioConfig>) => {
    setAudioConfigState((prev) => ({
      ...prev,
      ...updates,
    }));
  };

  const onSaveConfig = () => {
    try {
      const configToSave = {
        inputDevice: audioConfig.inputDevice,
        sampleRate: audioConfig.sampleRate,
        channels: audioConfig.channels,
      };
      localStorage.setItem(CONFIG_KEY, JSON.stringify(configToSave));
      toast.success("Audio settings saved");
    } catch (error) {
      console.error(error);
      toast.error("Failed to save audio settings");
    }
  };

  return {
    audioConfig,
    onUpdateAudioConfig,
    isLoading,
    devices,
    error,
    onSaveConfig,
    isSupported: isAudioSupported(),
  };
};
